/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// Component imports
import { Button, Card, Col, Empty, Row, Skeleton, Space, Spin, Typography } from 'antd';
import { ApartmentOutlined, ArrowLeftOutlined, CheckOutlined, LoadingOutlined } from '@ant-design/icons';

// Misc imports
import { useBotLanguage } from '../../utils/hooks/useBotLanguage';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useCreateNlpFlowMutation, useFetchNlpFlowQuery, useUpdateNlpFlowMutation } from '../../store/nlpFlows/nlpFlowsApi';
import FlowGraph from '../Graphs/FlowGraph';
import { DEFAULT_GRAPH_LAYOUT, NODE_TYPES } from '../../constants/flowGraph';
import { flowGraphToNlpFlow, nlpFlowToFlowGraph } from '../../utils/transform/flowGraph';
import { toggleFullScreenMode } from '../../store/global/globalActions';
import { useDispatch } from 'react-redux';
import { LANGUAGE_OPTIONS } from '../../constants/main';
import openConsoleAlertMessage from '../../utils/misc/openConsoleAlertMessage';

// Constant declarations
const { Text } = Typography;

const NlpFlowEditor = ({
  onBackToListClick = () => {}
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const { tenantId, orgId } = useParams();
  const { t } = useTranslation();
  const searchParams = new URLSearchParams(location.search);
  const { primaryBotLanguage } = useBotLanguage();

  const { data: bot, isFetching: isFetchingBot } = getCurrentQueryState('fetchBot', { orgId, tenantId }, false);
  const { currentData: nlpFlow, isLoading: isFetchingNlpFlow, error: fetchNlpFlowError } = useFetchNlpFlowQuery({
    orgId,
    tenantId: bot.id || tenantId,
    nlpFlowId: searchParams.get('nlpFlowId'),
    showError: false
  }, { skip: !searchParams.get('nlpFlowId') });
  const [, { isLoading: isCreatingNlpFlow }] = useCreateNlpFlowMutation({ fixedCacheKey: 'create-nlp-flow-shared' });
  const [updateNlpFlow, { isLoading: isUpdatingNlpFlow }] = useUpdateNlpFlowMutation({ fixedCacheKey: 'flowGraph-update-nlp-flow' });
  const [currentNlpFlowName, setCurrentNlpFlowName] = useState(nlpFlow?.name); // Keep NLP flow name in the state because we need to update it without refetching
  const [hasPendingChanges, setHasPendingChanges] = useState(false);

  const initialGraphElements = useMemo(() => {
    return nlpFlowToFlowGraph(nlpFlow, primaryBotLanguage) || [];
  }, [nlpFlow, searchParams.get('nlpFlowId'), primaryBotLanguage]);

  const initialGraphDirection = useMemo(() => {
    return nlpFlow?.content?.layout || DEFAULT_GRAPH_LAYOUT;
  }, [nlpFlow]);

  // On mount
  useEffect(() => {
    // Cleanup
    return () => {
      dispatch(toggleFullScreenMode(false));
    };
  }, []);

  // When there's a fetch error, navigate the user back to the NLP flows list
  useEffect(() => {
    // If the graph is not available, remove the NLP flow id from the url
    // This will navigate the user back to the NLP flows list
    if (!initialGraphElements && !isFetchingNlpFlow && fetchNlpFlowError === true) {
      if (searchParams.has('nlpFlowId')) {
        searchParams.delete('nlpFlowId');
        navigate({
          pathname: location.pathname,
          search: searchParams.toString()
        }, { replace: true });
      }
    }
  }, [fetchNlpFlowError]);

  useEffect(() => {
    setCurrentNlpFlowName(nlpFlow?.name);
  }, [nlpFlow]);

  // Handle the update of the activity display name
  const onNameSubmit = async (updatedName) => {
    if (updatedName && updatedName !== currentNlpFlowName) {
      const prevValue = updatedName;

      setCurrentNlpFlowName(updatedName);

      const { data: isSaveSuccessful } = await updateNlpFlow({ orgId, tenantId, nlpFlowId: nlpFlow.id, data: { name: updatedName } });

      // If save was not successful, fall back to the previous activity name
      if (!isSaveSuccessful) {
        setCurrentNlpFlowName(prevValue);
      }
    }
  };

  const onSaveElements = async (elements) => {
    try {
      const transformedData = flowGraphToNlpFlow(elements, nlpFlow.name, nlpFlow.language);
      const isSaveSuccessful = await updateNlpFlow({ orgId, tenantId, nlpFlowId: nlpFlow.id, data: transformedData });
      return isSaveSuccessful;
    } catch (error) {
      openConsoleAlertMessage({ type: 'error', message: error.message });
      return error;
    }
  };

  const renderNlpFlowName = () => {
    if (isFetchingNlpFlow) {
      return (
        <Skeleton.Input active className="d-block opacity-70 border-radius-base" />
      );
    }

    if (nlpFlow) {
      return (
        <Row gutter={16} align="middle">
          <Col flex="auto">
            <Typography.Title
              editable={{
                triggerType: currentNlpFlowName ? 'text' : 'icon',
                enterIcon: <CheckOutlined />,
                onChange: onNameSubmit,
                autoSize: { maxRows: 1, minRows: 1 }
              }}
              level={5}
              className="mb-0"
            >
              {currentNlpFlowName}
            </Typography.Title>
          </Col>

          {
            isUpdatingNlpFlow ? (
              <Col flex="none">
                <Spin indicator={<LoadingOutlined className="text-secondary" spin />} />
              </Col>
            ) : null
          }
        </Row>
      );
    }

    // Else
    return null;
  };

  return (
    <Fragment>
      <Space className="mb-3">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={onBackToListClick}
          type="primary"
        />

        <Typography.Text>
          {t('Back to NLP Flows')}
        </Typography.Text>
      </Space>

      <Card bordered={false} className="mb-5" loading={isFetchingBot && !bot}>
        <Row gutter={24} wrap={false} align="middle">
          <Col flex="none">
            <ApartmentOutlined className="fs-xxl text-primary" />
          </Col>
          <Col flex="auto">
            {renderNlpFlowName()}

            <Row className="my-1">
              <Col flex="auto">
                {
                  nlpFlow?.language ? (
                    <Text>
                      <Space size="small">
                        {t('NLP Flow Language:')}
                        {LANGUAGE_OPTIONS.find(lang => lang.key === nlpFlow.language)?.label || null}
                      </Space>
                    </Text>
                  ) : isFetchingNlpFlow ? (
                    <Skeleton.Input size="small" active className="d-block opacity-70 border-radius-base" />
                  ) : null
                }

              </Col>
            </Row>

            <Text>
              {t('Drag and drop new nodes and connect them to create flows. To remove an element, select it and press Backspace (on Windows & Linux) or Delete (on Mac).')}
            </Text>
          </Col>
        </Row>
      </Card>

      <Card bordered={false} className="mb-5" loading={isFetchingBot && !bot}>
        {
          isFetchingNlpFlow || isCreatingNlpFlow ? (
            <Row className="w-100">
              <Col flex="auto" className="text-center">
                <Spin className="mt-10 mb-8"/>
              </Col>
            </Row>
          ) : (
            nlpFlow && initialGraphElements ? (
              <FlowGraph
                hasPendingChanges={hasPendingChanges}
                setHasPendingChanges={setHasPendingChanges}
                graphId={nlpFlow.id}
                initialGraphElements={initialGraphElements}
                isLoading={isFetchingNlpFlow || isCreatingNlpFlow || isUpdatingNlpFlow}
                disabledNodeTypes={Object.values(NODE_TYPES).filter(nodeType => nodeType !== NODE_TYPES.QUERY && nodeType !== NODE_TYPES.PIPELINE)}
                onUpdate={onSaveElements}
                initialGraphDirection={initialGraphDirection}
                allowLayoutChange={false}
              />
            ) : (
              <Empty
                description={<Text type="secondary">{t('This NLP flow is not available, please go back to the list to create one')}.</Text>}
              />
            )
          )
        }
      </Card>
    </Fragment>
  );
};

NlpFlowEditor.propTypes = {
  onBackToListClick: PropTypes.func
};

export default NlpFlowEditor;
